"use client";
import styles from "./index.module.scss";
import { useEffect, useRef, useState } from "react";
import { Image, Input, Menu, Popover } from "antd";
import { AppstoreOutlined, RightOutlined, MessageFilled } from "@ant-design/icons";
import CancelAuthModal from "@/components/CancelAuthModal";
import SizeBox from "@/components/SizeBox";
import { getQueryString, isMobile } from "@/utils/action";
import Footer from "@/components/Footer";
import useLanguage from "@/hooks/useLanguage";

function getItem(label, key, icon, children, type, body) {
  return {
    key,
    icon,
    children,
    label,
    type,
    body
  };
}

function flattenSecondaryItems(items) {
  const result = [];

  function traverse(item) {
    if (item.children && item.children.length > 0) {
      item.children.forEach(child => {
        // 使用正则表达式去掉label前的数字
        const cleanLabel = child.label.replace(/^\d+\.\d+\s*/, '');
        result.push({ title: cleanLabel, key: child.key });
      });
    }
  }

  items.forEach(traverse);
  return result;
}

function waitForElement(id, timeout = 1000) {
  return new Promise((resolve, reject) => {
    const start = Date.now();

    const check = () => {
      const el = document.getElementById(id);
      if (el) return resolve(el);

      if (Date.now() - start > timeout) return reject('Element not found');
      requestAnimationFrame(check);
    };

    check();
  });
}

const waitForImagesLoaded = () => {
  return new Promise((resolve) => {
    if (document.readyState === 'complete') {
      // 页面和图片都加载完了
      resolve();
    } else {
      window.addEventListener('load', () => {
        resolve();
      });
    }
  });
};


const waitForElementStable = (id, maxWaitTime = 5000) => {
  return new Promise((resolve, reject) => {
    let lastHeight = 0;
    let sameCount = 0;
    const start = Date.now();

    const check = () => {
      const el = document.getElementById(id);
      if (!el) {
        if (Date.now() - start > maxWaitTime) {
          reject();
        } else {
          requestAnimationFrame(check);
        }
        return;
      }

      const currentHeight = el.offsetHeight;
      if (currentHeight === lastHeight) {
        sameCount++;
      } else {
        sameCount = 0;
        lastHeight = currentHeight;
      }

      if (sameCount >= 10) {
        // 连续10次高度没变，认为稳定了
        resolve(el);
      } else if (Date.now() - start > maxWaitTime) {
        reject();
      } else {
        requestAnimationFrame(check);
      }
    };

    check();
  });
};



const HelpCenter = () => {
  const { t } = useLanguage();
  const [isClient, setIsClient] = useState(false);
  const [mobile, setMobile] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [currentIndex, setCurrentIndex] = useState("0");
  const [randomList, setRandomList] = useState([]);
  const [outLoginVisible, setOutLoginVisible] = useState(false);

  const items = [
    {
      key: '0',
      icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/1.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
      label: t('help_center.welcome'),
      children: null
    },
    {
      key: '1',
      icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/6.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
      label: t('help_center.sections.new_user_guidance'),
      children: [{
        key: '1.1',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/7.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.select_products'),
        text: <div>
          <div>{t('help_center.content.select_products_description')}</div>
          <div className={`${styles.guideBox}`}>
            <div>
              <Image className={"lg:!h-[700px] lg:!w-full"} src={"https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/search1.png"} />
            </div>
            <div>
              <Image className={"lg:!h-[700px] lg:!w-full"} src={"https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/search2.png"} />
            </div>
          </div>
        </div>
      },
      {
        key: '1.2',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/8.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.add_to_showcase'),
        text: <div className={styles.contentText}>{t('help_center.content.add_to_showcase_description')} <br />
          <ul>
            <li>{t('help_center.content.add_to_showcase_step1')}</li>
            <li>{t('help_center.content.add_to_showcase_step2')}</li>
          </ul>
          <div className={styles.guideBox}>
            <div>
              <Image className={"lg:!h-[700px] lg:!w-full"} src={"https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/showcase1.png"} />
            </div>
            <div>
              <Image className={"lg:!h-[700px] lg:!w-full"} src={"https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/showcase2.png"} />
            </div>
          </div>
        </div>
      },
      {
        key: '1.3',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/9.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.get_samples'),
        text: <div>
          {t('help_center.content.get_samples_description')}<br />
          <div className={styles.guideBox}>
            <div>
              <Image className={"lg:!h-[700px] lg:!w-full"} src={"https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/samples1.png"} />
            </div>
            <div>
              <Image className={"lg:!h-[700px] lg:!w-full"} src={"https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/samples2.jpg"} />
            </div>
            <div>
              <Image className={"lg:!h-[700px] lg:!w-full"} src={"https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/samples3.jpg"} />
            </div>
          </div>
        </div>
      },
      {
        key: '1.4',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/10.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.unlock_highest_commission'),
        text: <div>{t('help_center.content.unlock_commission_description')}</div>
      },
      {
        key: '1.5',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/11.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.increase_commission'),
        text: t('help_center.content.increase_commission_description')
      }]
    },
    {
      key: '2',
      icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/12.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
      label: t('help_center.sections.qa_accounts'),
      children: [{
        key: '2.1',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/13.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.login_fail'),
        text: <div>
          {t('help_center.content.login_fail_description')}<br />
          <div className={styles.guideBox}>
            <div>
              <Image className={"lg:!h-[700px] lg:!w-full"} src={"https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/login1.jpg"} />
            </div>
            <div>
              <Image className={"lg:!h-[700px] lg:!w-full"} src={"https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/login2.jpg"} />
            </div>
          </div>
        </div>
      },
      {
        key: '2.2',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/14.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.authorize_cancel'),
        text: <div className={styles.contentText}>
          <div style={{ fontWeight: '700' }}>{t('help_center.content.authorize_title')}</div>
          <div className={styles.guideBox}>
            <div>
              <Image className={"lg:!h-[700px] lg:!w-full"} src={"https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/authorize1.jpg"} />
            </div>
            <div>
              <Image className={"lg:!h-[700px] lg:!w-full"} src={"https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/authorize2.jpg"} />
            </div>
          </div>
          <div style={{ fontWeight: '700' }}>{t('help_center.content.cancel_authorization_title')}</div>
          <div>{t('help_center.content.cancel_authorization_description')}</div>
          <ul>
            <li>{t('help_center.content.cancel_authorization_warning1')}</li>
            <li>{t('help_center.content.cancel_authorization_warning2')}</li>
          </ul>
          <div className={styles.cancel} onClick={() => setOutLoginVisible(!outLoginVisible)}>{t('help_center.content.cancel_authorization_button')}</div>
        </div>
      }]
    },
    {
      key: '3',
      icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/15.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
      label: t('help_center.sections.qa_creator_program'),
      children: [{
        key: '3.1',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/16.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.why_join_program'),
        text: <div className={styles.contentText}>
          <div>{t('help_center.content.why_join_program_description')}</div>
          <ul>
            <li>{t('help_center.content.join_program_benefits.highest_commissions')}</li>
            <li>{t('help_center.content.join_program_benefits.offline_samples')}</li>
            <li>{t('help_center.content.join_program_benefits.exclusive_boost')}</li>
            <li>{t('help_center.content.join_program_benefits.personalized_support')}</li>
            <li>{t('help_center.content.join_program_benefits.flash_sales')}</li>
          </ul>
        </div>
      },
      {
        key: '3.2',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/17.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.how_join_program'),
        text: <div>
          {t('help_center.content.how_join_program_description')}
          <ol>
            <li>{t('help_center.content.join_program_step1')}</li>
            <li>{t('help_center.content.join_program_step2')}</li>
            <li>{t('help_center.content.join_program_step3')}</li>
          </ol>
        </div>
      }]
    },
    {
      key: '4',
      icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/18.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
      label: t('help_center.sections.qa_samples'),
      children: [{
        key: '4.1',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/19.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.no_sample_button'),
        text: <div>
          <div style={{ fontWeight: '700' }}>{t('help_center.content.no_sample_button_reasons')}</div>
          <ol>
            <li> {t('help_center.content.no_sample_reason1')} </li>
          </ol>
          <div style={{ textIndent: '2em' }}>a. {t('help_center.content.eligibility_thresholds.follower_count')}</div>
          <div style={{ textIndent: '2em' }}>b. {t('help_center.content.eligibility_thresholds.gmv')}</div>
          <div style={{ textIndent: '2em' }}>c. {t('help_center.content.eligibility_thresholds.video_views')}</div>
          <div style={{ textIndent: '2em' }}>d. {t('help_center.content.eligibility_thresholds.category')}</div>
          <div style={{ textIndent: '2em' }}>e. {t('help_center.content.eligibility_thresholds.fulfillment_rank')}</div>
          <div>2. {t('help_center.content.no_sample_reason2')}</div>
          <div style={{ fontWeight: '700', marginTop: '20px' }}>{t('help_center.content.other_ways_title')}</div>
          <ol>
            <li>{t('help_center.content.other_ways_step1')}</li>
            <li>{t('help_center.content.other_ways_step2')}</li>
            <li>{t('help_center.content.other_ways_step3')}</li>
            <li>{t('help_center.content.other_ways_step4')}</li>
          </ol>
          <div style={{ fontWeight: '700', marginTop: '20px' }}>{t('help_center.content.quota_depends_title')}</div>
          <ol>
            <li>{t('help_center.content.quota_less_5000')}</li>
            <li>{t('help_center.content.quota_5000_150000')}</li>
            <li>{t('help_center.content.quota_over_150000')}</li>
          </ol>
          <div>{t('help_center.content.quota_note')}</div>
        </div>
      },
      {
        key: '4.2',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/20.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.request_offline_sample'),
        text: <div>
          <div>{t('help_center.content.request_offline_description')}</div>
          <ol>
            <li>{t('help_center.content.request_offline_step1')}</li>
            <li>{t('help_center.content.request_offline_step2')}</li>
            <li>{t('help_center.content.request_offline_step3')}</li>
            <li>{t('help_center.content.request_offline_step4')}</li>
          </ol>
          <div>{t('help_center.content.offline_sample_notification')}</div>
          <div style={{ fontWeight: '700' }}>{t('help_center.content.please_note')}</div>
          <ol>
            <li>{t('help_center.content.offline_sample_note1')}</li>
            <li>{t('help_center.content.offline_sample_note2')}</li>
          </ol>
        </div>
      },
      {
        key: '4.3',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/21.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.fulfill_sample_request'),
        text: <div>
          <div>{t('help_center.content.fulfill_sample_description')}</div>
          <div style={{ fontWeight: '700' }}>{t('help_center.content.please_note')}</div>
          <div>{t('help_center.content.fulfill_sample_note')}</div>
        </div>
      },
      {
        key: '4.4',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/22.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.samples_out_of_stock'),
        text: <div className={styles.contentText}>
          <ul>
            <li>{t('help_center.content.samples_out_of_stock_step1')}</li>
            <li>{t('help_center.content.samples_out_of_stock_step2')}</li>
            <li>{t('help_center.content.samples_out_of_stock_step3')}</li>
          </ul>
        </div>
      }]
    },
    {
      key: '5',
      icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/23.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
      label: t('help_center.sections.qa_products'),
      children: [{
        key: '5.1',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/24.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.product_quality'),
        text: t('help_center.content.product_quality_description')
      },
      {
        key: '5.2',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/25.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.wrong_product'),
        text: <div>
          <div>{t('help_center.content.wrong_product_description')}</div>
          <ol>
            <li>{t('help_center.content.wrong_product_reason1')}</li>
            <li>{t('help_center.content.wrong_product_reason2')}</li>
          </ol>
        </div>
      },
      {
        key: '5.3',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/26.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.fulfillment_message'),
        text: t('help_center.content.fulfillment_message_description')
      },
      {
        key: '5.4',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/27.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.broken_product'),
        text: t('help_center.content.broken_product_description')
      }]
    },
    {
      key: '6',
      icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/28.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
      label: t('help_center.sections.qa_commissions'),
      children: [{
        key: '6.1',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/29.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.higher_commission_not_working'),
        text: t('help_center.content.higher_commission_not_working_description')
      },
      {
        key: '6.2',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/30.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.get_higher_commission'),
        text: <div>
          <div>{t('help_center.content.get_higher_commission_description')}</div>
          <ol>
            <li>{t('help_center.content.higher_commission_method1')}</li>
            <li>{t('help_center.content.higher_commission_method2')}</li>
            <li>{t('help_center.content.higher_commission_method3')}</li>
            <li>{t('help_center.content.higher_commission_method4')}</li>
          </ol>
        </div>
      },
      {
        key: '6.3',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/31.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.get_ad_code'),
        text: <div>
          <div>{t('help_center.content.get_ad_code_description')}</div>
          <div>{t('help_center.content.get_ad_code_steps')}</div>
          <div>{t('help_center.content.get_ad_code_step1')}</div>
          <div>{t('help_center.content.get_ad_code_step2')}</div>
          <div>-{t('help_center.content.get_ad_code_step3')}</div>
          <div>-{t('help_center.content.get_ad_code_step4')}</div>
          <div>-{t('help_center.content.get_ad_code_step5')}</div>
          <div>-{t('help_center.content.get_ad_code_step6')}</div>
          <div>{t('help_center.content.get_ad_code_note')}</div>
          <Image src={"https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help_center.jpeg"} />
        </div>
      }]
    },
    {
      key: '7',
      icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/32.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
      label: t('help_center.sections.qa_logistics'),
      children: [{
        key: '7.1',
        icon: <img src={`https://anystarr-image.oss-ap-southeast-1.aliyuncs.com/help-center/33.png`} style={{ marginRight: "5px" }} width={25} height={25} />,
        label: t('help_center.sections.package_not_received'),
        text: t('help_center.content.package_not_received_description')
      }]
    },
  ];
  function getRandomItems(list, count) {
    const shuffled = [...list].sort(() => 0.5 - Math.random()); // 打乱数组顺序
    return shuffled.slice(0, count); // 取前 `count` 个元素
  }

  useEffect(() => {
    setIsClient(true);
    setMobile(isMobile());
    setShowMenu(!isMobile());
  }, []);
  useEffect(() => {
    const flatList = flattenSecondaryItems(items);
    setRandomList(getRandomItems(flatList, 4));
  }, [currentIndex]);


  useEffect(() => {
    const key = getQueryString("key");
    setTimeout(()=>{
      onMenuClick({ key });
    }, 1200);
  }, []);

  const onMenuClick = async (e) => {
    setCurrentIndex(e.key);
    if (mobile) {
      setShowMenu(false);
    }
    await waitForImagesLoaded(); // 💥 等图片全部加载完！

    setTimeout( async  () => {
      const target = await waitForElementStable(e.key);
      if (target) {
        // const headerHeight = document.querySelector('.navbar').offsetHeight; // 获取导航栏的高度

        // 使用 scrollIntoView 滚动到目标元素，并添加偏移量
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest'
        });

        // 调整页面位置，确保目标元素下方有足够空间
        // window.scrollBy(0, -200); // 向上滚动导航栏的高度，确保目标元素下方有空间
      }
    }, 0);
  };

  if (!isClient) {
    return null; // Show a loading indicator or null during server-side rendering
  }


  const openEmail = () => {
    const email = 'contact@anystarr.com';
    const subject = t('help_center.email_subject');
    const body = '';

    // 使用mailto协议打开默认的电子邮件客户端
    const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    // 打开邮件客户端
    window.location.href = mailtoLink;
  };

  return <div className={styles.help_page}>
    <div className={`${styles.nav_wrap} ${showMenu ? styles.show_menu : ""}`}>
      <div>
        <Menu
          inlineCollapsed={!showMenu}
          onClick={onMenuClick}
          defaultSelectedKeys={['0']}
          defaultOpenKeys={['0']}
          mode={"inline"}
          items={items}
        />

        {
          mobile && <div onClick={() => setShowMenu(!showMenu)} className={`${showMenu ? styles.menuBotton : styles.unShowMenu}`}>
            {
              showMenu ? t('help_center.fold_up_navigation') : <RightOutlined style={{ color: '#fff' }} />
            }
          </div>
        }
      </div>
      {
        !mobile && <div onClick={openEmail} className={styles.contact_us_txt}> {t('help_center.contact_us')} </div>
      }
    </div>
    <div id={"body-warp"} className={`${styles.body_wrap} ${showMenu ? styles.body_wrap_show_menu : ""}`}>

      {
        currentIndex === "0" ? <>
          <WelcomeComp mobile={mobile} onMenu={(item) => onMenuClick(item)} randomList={randomList} items={items}></WelcomeComp>
          <SizeBox h={30}></SizeBox>
          <div className={styles.popular_wrap}>
            <div className={styles.popular_title}>{t('help_center.popular_question')}</div>
            {
              randomList.map((item, index) => {
                return <div key={index} onClick={() => onMenuClick(item)} className={styles.popular_item}>{item.title}</div>;
              })
            }
          </div>
          <SizeBox h={30}></SizeBox>
        </> : <div className={styles.normal_wrap}>
          {
            items.map((item, index) => {
              return item.label !== t('help_center.welcome') && (
                <div key={index}>
                  <SectionTitle1 id={item.key}>
                    {item.label}
                  </SectionTitle1>
                  {
                    item.children?.map((child, index) => {
                      console.log('~ child', child);
                      return <div key={index}>
                        <SectionTitle2 id={child.key}>
                          {child.label}
                        </SectionTitle2>
                        <SectionTitle3>
                          {child.text}
                        </SectionTitle3>
                      </div>;
                    })
                  }
                  <SizeBox h={50}></SizeBox>
                </div>
              );
            })
          }
          <SizeBox h={100}></SizeBox>
        </div>
      }
      {
        !mobile &&  <div>
          <Footer className={"px-[20px]"}></Footer>
        </div>
      }
    </div>
    {/* <div onClick={openEmail} className={styles.contact_us}>*/}
    {/*  <MessageFilled />*/}
    {/* </div>*/}
    <CancelAuthModal show={outLoginVisible} onCancel={() => setOutLoginVisible(false)}></CancelAuthModal>
  </div >;
};


const WelcomeComp = ({ onMenu, randomList, items, mobile }) => {
  const { t } = useLanguage();
  const [currentSearchList, setCurrentSearchList] = useState([]);

  const topics = [
    { title: t('help_center.topics.new_influencers_guidance'), key: "1", description: t('help_center.topics.what_is_anystarr_desc'), icon: 'https://static.xx.fbcdn.net/assets/?revision=1431132240730458&name=Illustrations-Get-Started&density=1' },
    { title: t('help_center.topics.qa_accounts_desc'), key: "2", description: t('help_center.topics.qa_accounts_desc'), icon: 'https://static.xx.fbcdn.net/assets/?revision=1431132240730458&name=Illustrations-Security&density=1' },
    { title: t('help_center.sections.qa_samples'), key: "3", description: t('help_center.topics.qa_samples_desc'), icon: 'https://static.xx.fbcdn.net/assets/?revision=1431132240730458&name=Illustrations-Backup&density=1' },
    { title: t('help_center.sections.qa_products'), key: "4", description: t('help_center.topics.qa_products_desc'), icon: 'https://static.xx.fbcdn.net/assets/?revision=1431132240730458&name=Illustrations-Backup&density=1' },
    { title: t('help_center.sections.qa_commissions'), key: "5", description: t('help_center.topics.qa_commissions_desc'), icon: 'https://static.xx.fbcdn.net/assets/?revision=1431132240730458&name=Illustrations-Backup&density=1' },
    { title: t('help_center.sections.qa_logistics'), key: "6", description: t('help_center.topics.qa_logistics_desc'), icon: 'https://static.xx.fbcdn.net/assets/?revision=1431132240730458&name=Illustrations-Backup&density=1' },
  ];

  const onMenuClick = (e) => {
    window.location.href = `#${e.key}`;
  };

  const content = (
    <div style={{
      width: mobile ? 200 : 500
    }}>
      {
        currentSearchList.length > 0 ? currentSearchList.map((item, index) => {
          return <div key={index} style={{
            height: "40px",
            lineHeight: "40px",
            cursor: "pointer"
          }} onClick={() => onMenu(item)}>{item.title}</div>;
        }) : randomList.map((item, index) => {
          return <div key={index} style={{
            height: mobile ? "unset" : "40px",
            lineHeight: mobile ? "20px" : "40px",
            marginBottom: mobile ? 10 : 5,
            cursor: "pointer"
          }} onClick={() => onMenu(item)}>{item.title}</div>;
        })
      }
    </div>
  );

  const onChange = (e) => {
    if (!e.target.value) {
      setCurrentSearchList([]);
      return;
    }
    const cl = flattenSecondaryItems(items);
    const searchValue = e.target.value.toLowerCase(); // 将输入值转换为小写，确保不区分大小写

    // 筛选出与 searchValue 模糊匹配的内容
    const filteredList = cl.filter(item =>
      item.title.toLowerCase().includes(searchValue)
    );
    // 将筛选后的结果设置到 currentSearchList 中
    setCurrentSearchList(filteredList);
  };

  return (
    <div id={"0"} className={styles.welcome_container}>
      <h1>{t('help_center.welcome')}</h1>
      <SizeBox h={30} />
      <h1 className={styles.welcome_heading}>{t('help_center.what_is_anystarr')}</h1>
      <SectionTitle3 className={styles.welcome_cardDescription}>{t('help_center.platform_description')}</SectionTitle3>
      <SectionTitle3 className={styles.welcome_cardDescription}>{t('help_center.platform_description_long')}</SectionTitle3>
      <SizeBox h={30} />
      <h1 className={styles.welcome_heading}>{t('help_center.how_can_we_help')}</h1>
      <Popover overlayInnerStyle={{
        width: mobile ? 210 : 500
      }} placement={"bottomLeft"} trigger={"click"} content={content}>
        <div className={styles.welcome_search}>
          <Input onChange={onChange} type={"text"} placeholder={t('help_center.search_placeholder')} className={styles.welcome_searchInput} />
        </div>
      </Popover>
      <h4 className={styles.welcome_heading}>{t('help_center.popular_topics')}</h4>
      <div className={styles.welcome_topics}>
        {topics.map((topic, index) => (
          <div onClick={() => onMenuClick(topic)} key={index} className={styles.welcome_card}>
            {/* <img src={topic.icon} alt={topic.title} className={styles.welcome_icon} />*/}
            <h3 className={styles.welcome_cardTitle}>{topic.title}</h3>
            <p className={styles.welcome_cardDescription}>{topic.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
};


const SectionTitle1 = ({ id, children }) => {
  return <div id={id} className={styles.section_title_1}>{children}</div>;
};

const SectionTitle2 = ({ id, children }) => {
  return <div id={id} className={styles.section_title_2}>{children}</div>;
};

const SectionTitle3 = ({ id, children }) => {
  return <div id={id} className={styles.section_title_3}>{children}</div>;
};

const HelpCenterIcon = ({ id }) => {
  return <img src={`helpCenter/${id}.png`} style={{ marginRight: "5px" }} width={25} height={25} />;
};

export default HelpCenter;
